import React, { useState, useEffect } from 'react';
import { Trash2, Eye, Edit, ChevronDown, Filter, Search, Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import CreateArticleModal from '@/components/modals/CreateArticleModal';

interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
  categoryColor: string;
  status: 'Published' | 'Draft' | 'Scheduled';
  lastUpdated: string;
  author: string;
}

const AdminHelpFaqsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('Customer Articles');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  // (audit 2026-06-10) — filter dropdowns previously had no onClick. Now
  // drive in-memory filtering on the article list further below.
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  // (audit 2026-06-11) — pagination buttons (1 2 3 → ←) had no onClick.
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const filteredArticles = articles
    .filter((a) => categoryFilter === 'all' || (a as any).category === categoryFilter)
    .filter((a) => statusFilter === 'all' || (a as any).status === statusFilter)
    .filter((a) => {
      const q = searchText.trim().toLowerCase();
      return q ? String((a as any).title || '').toLowerCase().includes(q) : true;
    });
  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filteredArticles.length);
  const pagedArticles = filteredArticles.slice(pageStart, pageEnd);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiService.request('/admin/faqs', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped = response.data.map((d: any) => ({
            id: d.id,
            title: d.question || d.title || '',
            slug: d.slug || `/${d.category?.toLowerCase()}/${d.id}`,
            category: d.category || 'General',
            categoryColor: getCategoryColor(d.category || 'General'),
            status: (d.published ? 'Published' : 'Draft') as 'Published' | 'Draft' | 'Scheduled',
            lastUpdated: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : 'N/A',
            author: d.created_by || 'Admin'
          }));
          setArticles(mapped);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Billing': 'bg-blue-100 text-blue-800',
      'Scheduling': 'bg-purple-100 text-purple-800',
      'Compliance': 'bg-orange-100 text-orange-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { name: 'Customer Articles', count: articles.length },
    { name: 'Provider Articles', count: articles.filter(a => a.category === 'Provider').length },
    { name: 'FAQs', count: articles.filter(a => a.category === 'FAQ').length }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Published':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler: Create FAQ
  const handleCreateFaq = async (faqData: any) => {
    try {
      const response = await apiService.request('/admin/faqs', 'POST', {
        ...faqData,
        createdBy: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ FAQ created');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Edit FAQ
  const handleEditFaq = async (faqId: string, faqData: any) => {
    try {
      const response = await apiService.request(`/admin/faqs/${faqId}`, 'PUT', {
        ...faqData,
        modifiedBy: localStorage.getItem('adminId'),
        modifiedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ FAQ updated');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Delete FAQ
  const handleDeleteFaq = async (faqId: string) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      const response = await apiService.request(`/admin/faqs/${faqId}`, 'DELETE', {
        deletedBy: localStorage.getItem('adminId'),
        deletedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ FAQ deleted');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Publish FAQ
  const handlePublishFaq = async (faqId: string) => {
    try {
      const response = await apiService.request(`/admin/faqs/${faqId}/publish`, 'POST', {
        publishedBy: localStorage.getItem('adminId'),
        publishedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ FAQ published');
        window.location.reload();
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  // Handler: Export FAQs
  const handleExportFaqs = async () => {
    try {
      const response = await apiService.request('/admin/faqs/export', 'GET');
      if (response.success && response.data) {
        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `faqs-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        alert('✓ FAQs exported');
      } else alert('Error: ' + response.message);
    } catch (error: any) {
      alert('Failed: ' + error.message);
    }
  };

  return (
    <AdminLayout>
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Help Centre Articles</h1>
          <p className="text-gray-600 mb-6">Manage Customer and Provider support content and maintain documentation standards across the ecosystem.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-8 mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => setActiveTab(tab.name)}
              className={`px-1 py-3 text-sm font-medium transition-colors relative flex items-center gap-2 ${
                activeTab === tab.name
                  ? 'text-teal-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.name}
              <span className="text-xs font-semibold text-gray-400">{tab.count}</span>
              {activeTab === tab.name && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600"></div>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Filter by title or author..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <option value="all">All Categories</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="service">Service</option>
            <option value="other">Other</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <option value="all">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <button
            onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); setSearchText(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            title="Reset filters"
          >
            <Filter className="w-4 h-4" />
            More Filters
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="ml-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            ⊕ Create Article
          </button>
        </div>

        {/* Table */}
        <Card className="mb-6">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-3 px-4 text-left">
                      <input type="checkbox" className="rounded" />
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Article Title</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Last Updated</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Author</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedArticles.map((article) => (
                    <tr key={article.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input type="checkbox" className="rounded" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{article.title}</div>
                        <div className="text-xs text-gray-500">Slug: {article.slug}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                          <Badge className={article.categoryColor}>{article.category}</Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(article.status)}>
                          {article.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{article.lastUpdated}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-700 font-semibold">
                            {article.author.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-600">{article.author}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                          <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <p>
                {filteredArticles.length === 0
                  ? 'NO MATCHING ARTICLES'
                  : `SHOWING ${pageStart + 1}-${pageEnd} OF ${filteredArticles.length} ARTICLES`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                  <button
                    key={n}
                    onClick={() => setCurrentPage(n)}
                    className={`px-2 py-1 border border-gray-300 rounded ${
                      n === currentPage ? 'bg-teal-600 text-white' : 'hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase font-semibold mb-1">Total Readers (30D)</p>
              <p className="text-4xl font-bold text-gray-900">42,891</p>
              <p className="text-sm text-green-600 mt-2">↗ +12.4% from last month</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase font-semibold mb-1">Average Rating</p>
              <div className="flex items-center gap-2 mt-2">
                <p className="text-4xl font-bold text-gray-900">4.82</p>
                <span className="text-lg text-gray-600">/5.0</span>
              </div>
              <div className="flex gap-0.5 mt-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i <= 4 ? 'fill-yellow-400 text-yellow-400' : 'text-yellow-300'}`}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-xs uppercase font-semibold mb-1">Most Searched Term</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">&quot;API Refund&quot;</p>
              <p className="text-sm text-gray-600 mt-2">MATCHES 4 ARTICLES</p>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
      <CreateArticleModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
    </AdminLayout>
  );
};

export default AdminHelpFaqsPage;
